import React from 'react';

// v9 .sumcard tones (BASYS override block). Literals are the values v9 hard-codes.
export const SUMCARD_TONES = {
	current: {
		card: 'bg-gradient-to-br from-[#f1f5f9] to-[#e2e8f0] border-[#cbd5e1]',
		label: 'text-[#475569]',
		bar: 'bg-red-500',
		value: 'text-gray-900',
		sub: 'text-[#64748b]',
	},
	proposed: {
		card: 'bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200',
		label: 'text-blue-700',
		bar: 'bg-blue-500',
		value: 'text-gray-900',
		sub: 'text-blue-700',
	},
	savings: {
		card: 'bg-gradient-to-br from-[#f4fce6] to-[#e4f7c2] border-[#d1f19b]',
		label: 'text-[#5a8718]',
		bar: 'bg-[#a2e63c]',
		value: 'text-[#2b410b]',
		sub: 'text-[#5a8718]',
	},
	loss: {
		card: 'bg-gradient-to-br from-red-50 to-red-100 border-red-200',
		label: 'text-red-700',
		bar: 'bg-red-500',
		value: 'text-red-900',
		sub: 'text-red-700',
	},
	profit: {
		card: 'bg-gradient-to-br from-primary-500 to-[#1a1f80] border-primary-500 cursor-pointer hover:shadow-[0_0_0_3px_#2ea3f2] focus-visible:shadow-[0_0_0_3px_#2ea3f2] focus-visible:outline-none',
		label: 'text-[#b9bce0]',
		bar: 'bg-[#ff9d1c]',
		value: 'text-white',
		sub: 'text-[#b9bce0]',
	},
} as const;

type Tone = (typeof SUMCARD_TONES)[keyof typeof SUMCARD_TONES];

interface SummaryCardProps {
	tone: Tone;
	label: string;
	v1: string;
	l1: string;
	v2: string;
	l2: string;
	loading?: boolean;
	onClick?: () => void;
}

/** One v9 summary card: label on top, two figures side by side. */
const SummaryCard: React.FC<SummaryCardProps> = ({
	tone,
	label,
	v1,
	l1,
	v2,
	l2,
	loading = false,
	onClick,
}) => (
	<div
		className={`relative grid grid-cols-2 items-end gap-x-4 gap-y-0.5 overflow-hidden rounded-xl border px-4 py-3 before:absolute before:-right-12 before:-top-12 before:h-24 before:w-24 before:rounded-full before:bg-white/35 ${tone.card}`}
		{...(onClick && {
			role: 'button',
			tabIndex: 0,
			onClick,
			onKeyDown: (event: React.KeyboardEvent) => {
				if (event.key === 'Enter' || event.key === ' ') {
					event.preventDefault();
					onClick();
				}
			},
		})}
	>
		<div
			className={`relative col-span-2 mb-3 flex items-center gap-2 text-[10px] font-bold uppercase tracking-[.12em] ${tone.label}`}
		>
			<i className={`h-3 w-1 rounded-sm ${tone.bar}`} />
			{label}
		</div>
		{loading ? (
			<div className="relative col-span-2 h-12 animate-pulse rounded bg-white/50" />
		) : (
			<>
				<div
					className={`relative text-[22px] font-bold leading-[1.1] ${tone.value}`}
				>
					{v1}
				</div>
				<div
					className={`relative text-[22px] font-bold leading-[1.1] ${tone.value}`}
				>
					{v2}
				</div>
				<div
					className={`relative mt-1 whitespace-nowrap text-xs font-medium uppercase tracking-[.08em] ${tone.sub}`}
				>
					{l1}
				</div>
				<div
					className={`relative mt-1 whitespace-nowrap text-xs font-medium uppercase tracking-[.08em] ${tone.sub}`}
				>
					{l2}
				</div>
			</>
		)}
	</div>
);

export default SummaryCard;
